import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";

const minutesToMs = (value, defaultMinutes) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return defaultMinutes * 60 * 1000;
  }
  return minutes * 60 * 1000;
};

/** Show warning after this many minutes of no user activity */
const IDLE_TIMEOUT_MS = minutesToMs(
  process.env.REACT_APP_SESSION_IDLE_TIMEOUT_MINUTES,
  15
);

/** Auto-logout if user does not respond to the warning within this many minutes */
const CONSENT_TIMEOUT_MS = minutesToMs(
  process.env.REACT_APP_SESSION_CONSENT_TIMEOUT_MINUTES,
  2
);

const CHECK_INTERVAL_MS = 1000;

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

/** Persisted so a page reload continues the countdown instead of restarting it */
const IDLE_STORAGE_KEYS = {
  lastActivityAt: "PortalAdmin-Session-LastActivityAt",
  warningShownAt: "PortalAdmin-Session-WarningShownAt",
  authKey: "PortalAdmin-Session-AuthKey",
};

/** How often an activity event is written to storage, at most */
const ACTIVITY_PERSIST_INTERVAL_MS = 1000;

const readStoredValue = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const readStoredTimestamp = (key) => {
  const value = Number(readStoredValue(key));
  return Number.isFinite(value) && value > 0 ? value : null;
};

const writeStoredValue = (key, value) => {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, String(value));
    }
  } catch (error) {
    // Storage unavailable (private mode); idle tracking falls back to memory only.
  }
};

const clearStoredIdleState = () => {
  writeStoredValue(IDLE_STORAGE_KEYS.lastActivityAt, null);
  writeStoredValue(IDLE_STORAGE_KEYS.warningShownAt, null);
};

/** Changes on every new sign-in, so a fresh login never inherits an old idle clock */
const getAuthKey = (user) =>
  `${user?.profile?.sub || ""}:${user?.profile?.auth_time || ""}`;

const clearSessionStorage = () => {
  localStorage.setItem("logout", Date.now());
  localStorage.removeItem("ApplicationAccessId");
  localStorage.removeItem("PortalAdmin-AccessToken");
  localStorage.removeItem("PortalAdmin-AccessToken-temp");
  localStorage.removeItem("userSub");
  localStorage.removeItem("PatientIDHL7");
};

/**
 * Idle-timeout session warning.
 * Shows popup after no activity for IDLE_TIMEOUT_MS.
 * Logs out if the user does not choose Keep Working or Logout within CONSENT_TIMEOUT_MS.
 */
const useAutoLogout = (enabled = true) => {
  const { user, userManager, setUser } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false);

  const showWarningRef = useRef(false);
  const loggingOutRef = useRef(false);
  const lastActivityAtMsRef = useRef(Date.now());
  const warningShownAtMsRef = useRef(null);
  const lastPersistedActivityAtMsRef = useRef(0);

  const performLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setShowWarning(false);
    showWarningRef.current = false;
    warningShownAtMsRef.current = null;
    clearStoredIdleState();
    clearSessionStorage();
    navigate("/auth-callback-logout");
  }, [navigate]);

  const keepWorking = useCallback(async () => {
    if (isRenewing) return;
    setIsRenewing(true);
    try {
      const renewedUser = await userManager.signinSilent();
      if (renewedUser?.access_token) {
        localStorage.setItem(
          "PortalAdmin-AccessToken",
          renewedUser.access_token
        );
        if (typeof setUser === "function") {
          setUser(renewedUser);
        }
      }
      const now = Date.now();
      lastActivityAtMsRef.current = now;
      lastPersistedActivityAtMsRef.current = now;
      warningShownAtMsRef.current = null;
      showWarningRef.current = false;
      writeStoredValue(IDLE_STORAGE_KEYS.lastActivityAt, now);
      writeStoredValue(IDLE_STORAGE_KEYS.warningShownAt, null);
      setShowWarning(false);
    } catch (error) {
      console.error("Session renew failed:", error);
      performLogout();
    } finally {
      setIsRenewing(false);
    }
  }, [isRenewing, userManager, setUser, performLogout]);

  useEffect(() => {
    if (!enabled || !user) {
      setShowWarning(false);
      showWarningRef.current = false;
      warningShownAtMsRef.current = null;
      lastActivityAtMsRef.current = Date.now();
      return undefined;
    }

    loggingOutRef.current = false;
    lastActivityAtMsRef.current = Date.now();

    const onActivity = () => {
      if (loggingOutRef.current || showWarningRef.current) return;
      lastActivityAtMsRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    const tick = () => {
      if (loggingOutRef.current) return;

      const now = Date.now();

      if (showWarningRef.current) {
        const warningShownAtMs = warningShownAtMsRef.current;
        if (
          warningShownAtMs &&
          now - warningShownAtMs >= CONSENT_TIMEOUT_MS
        ) {
          performLogout();
        }
        return;
      }

      if (now - lastActivityAtMsRef.current >= IDLE_TIMEOUT_MS) {
        warningShownAtMsRef.current = now;
        showWarningRef.current = true;
        setShowWarning(true);
      }
    };

    const intervalId = window.setInterval(tick, CHECK_INTERVAL_MS);
    tick();

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
    };
  }, [enabled, user, performLogout]);

  return {
    showWarning,
    isRenewing,
    keepWorking,
    logout: performLogout,
  };
};

export default useAutoLogout;
